import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import uniq from 'lodash/uniq';

import { PageKeyMatch } from '../types/PageKeyMatch';
import { toPageKey } from './toPageKey';

/**
 * Builds the keys a page view is sent with, from React Router's `useMatches()`:
 * one match per route on the matched branch, outermost first. The last match
 * is the page; every match above it is a section, with `/*` added. Empty keys
 * are skipped and the page key always comes last, because that is the one
 * `sendPageView` counts.
 *
 * @example
 * // URL /applicant/3f2c…/submit-application
 * //
 * //   match pathname                         toPageKey                      becomes
 * //   /                                      ''                             (skipped)
 * //   /applicant/3f2c…                       'applicant'                    'applicant/*'
 * //   /applicant/3f2c…/submit-application    'applicant/submit-application' the page key
 * //
 * // → ['applicant/*', 'applicant/submit-application']
 *
 * // URL /sign-up, a section's index page, matches as the parent and as itself
 * // → ['sign-up/*', 'sign-up']
 *
 * // URL / has no page key
 * // → []
 */
export const getPageKeys = (matches: PageKeyMatch[]): string[] => {
  if (isEmpty(matches)) {
    return [];
  }

  const { params, pathname } = last(matches);
  const pageKey = toPageKey(pathname, params);

  if (!pageKey) {
    return [];
  }

  const sectionKeys = matches
    .slice(0, -1) // the matches above the page: '/', '/applicant/3f2c…'
    .map((match): string => toPageKey(match.pathname, match.params)) // ['', 'applicant']
    .filter((sectionKey): boolean => Boolean(sectionKey)) // ['applicant']
    .map((sectionKey): string => `${sectionKey}/*`); // ['applicant/*']

  return uniq([...sectionKeys, pageKey]);
};
